import {
  akitaConfig,
  EntityState,
  EntityStore,
  QueryEntity,
} from "@datorama/akita";
import produce from "immer";
import { Observable } from "rxjs";
import { debounceTime, map, withLatestFrom } from "rxjs/operators";
import { v4 } from "uuid";
import { Note } from "../data";
import { debug } from "console";
import { dummyNoteGenerator } from "../data/init";
import { useObservable, useObservableState } from "observable-hooks";

akitaConfig({ resettable: true });

export const noteCreator: () => Note = () => {
  return {
    id: v4(),
    title: "",
    version: 1,
    schedule: {
      type: "NONE",
    },
    completed: false,
    tags: [],
    background: "#cbecf7",
    created: new Date(),
  };
};

export interface NotesState extends EntityState<Note> {
  listOrders: { [listId: string]: string[] };
  undo:
    | {
        label: string;
        toRestore: Note;
      }
    | undefined;
  query: string;
}

export const removeNote = (id: string) => {
  console.log("updating remove note");
};

export class NotesStore extends EntityStore<NotesState> {
  setOrderFor = (id: string, order: string[]) => {
    this.update(
      produce((draft: NotesState) => {
        draft.listOrders[id] = order;
      })
    );
  };

  setUndo = (note: Note) => {
    this.update(
      produce((draft: NotesState) => {
        draft.undo = { toRestore: note, label: "Deletion" };
      })
    );
  };

  removeUndo = () => {
    this.update(
      produce((draft: NotesState) => {
        draft.undo = undefined;
      })
    );
  };

  constructor() {
    super(
      {
        listOrders: {},
        undo: undefined,
      },
      { name: "notes" }
    );
  }
}

export class NotesQuery extends QueryEntity<NotesState> {
  notes$ = this.selectAll();
  getOrderFor$ = (id: string) => {
    return this.select((state) => state.listOrders[id]);
  };
  constructor(protected store: NotesStore) {
    super(store);
  }
}

export const notesStore = new NotesStore();
export const notesQuery = new NotesQuery(notesStore);

// notesQuery
//   .getListFor$({ type: "BACKLOG" })
//   .subscribe({ next: (e) => console.log("love your energy", e) });

export const useAreaOrder = (areaId: string) => {
  const order$ = useObservable(() => notesQuery.getOrderFor$(areaId));
  const order = useObservableState(order$);
  return {
    order: order ?? [],
    setOrder: (o: string[]) => notesStore.setOrderFor(areaId, o),
  };
};

export const useNotes = () => {
  const notes$ = useObservable(() => notesQuery.notes$);
  const notes = useObservableState(notes$);
  return {
    notes: notes ?? [],
  };
};
export const useNote = (id: String) => {
  const note$ = notesQuery.selectEntity(id);
  const note = useObservableState(note$);

  return {
    note: note ?? noteCreator(),
  };
};

export const useNoteUndo = () => {
  const undo$ = notesQuery.select((q) => q.undo);
  const undo = useObservableState(undo$);

  const handleUndo = () => {
    notesStore.upsert(undo?.toRestore!.id, undo?.toRestore!);
    console.log("undoing");
  };

  const handleTimeout = () => {
    console.log("timing out");
    notesStore.removeUndo();
  };

  return {
    undoState: undo,
    undo: handleUndo,
    timeoutUndo: handleTimeout,
  };
};

export const useNotesOperations = () => {
  const updateNote = (n: Note) => {
    console.log("updating a note", n);
    notesStore.upsert(n.id, n);
  };
  const updateNoteById = (id: String, effector: (n: Note) => Note) => {
    console.log("updating note by id", id);
    notesStore.update(id, effector);
  };
  const updateNotes = (ns: Note[]) => {
    console.log("updating notes", ns);
    ns.forEach((n) => {
      notesStore.upsert(n.id, n);
    });
  };
  const removeNote = (id: String) => {
    console.log("removing note", id);
    notesStore.setUndo(notesQuery.getEntity(id)!);
    notesStore.remove(id);
  };
  const addNote = (n: Note) => {
    NEWEST_NOTE_ID = n.id;
    notesStore.add(n);
  };

  return {
    updateNote,
    updateNoteById,
    updateNotes,
    removeNote,
    addNote,
  };
};

export var NEWEST_NOTE_ID: string | undefined = undefined;
